import React, { KeyboardEvent, useEffect, useMemo, useState, useRef, memo } from "react";
import { Button, Form, Icon, Label, Input, Popup, Modal, FormGroup } from "semantic-ui-react";

// serializer
import CentralLabOrderSimplifiedSerializer from "issara-sdk/types/CentralLabOrderSimplifiedSerializer_apps_LAB";
import CentralLabOrderItemActionLogSerializer from "issara-sdk/types/CentralLabOrderItemActionLogSerializer_apps_LAB";
import SpecimenContainerSerializer from "issara-sdk/types/SpecimenContainerSerializer_apps_LAB";

// UX
import CardSpecimenCollector from "react-lib/apps/HISV3/LAB/CardSpecimenCollector";
import CardMeLabReport from "react-lib/apps/MEH/CardMeLabReport";
import CardMeLabOrderListUX from "react-lib/apps/MEH/CardMeLabOrderListUX";

// Common
import ModInfo from "react-lib/apps/common/ModInfo";

// Utils
import { useIntl } from "react-intl";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import {
  LabOrderQueueType,
  SpecimenCollectorMasterDataType,
} from "react-lib/apps/HISV3/LAB/LabInterface";
import { State as LabReportSequence } from "react-lib/apps/HISV3/LAB/sequence/LabReport";
import { STATUS_OPTIONS } from "../HISV3/LAB/CardLabOrderQueue";

const CARD_KEY = "CardMeLabOrder";

type CardMeLabOrderProps = {
  setProp: any;
  runSequence: any;
  onEvent: any;
  buttonLoadCheck?: Record<string, any>;
  selectedDivision?: Record<string, any>;
  selectedPatient?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  errorMessage?: Record<string, any>;
  successMessage?: Record<string, any>;
  django?: Record<string, any>;
  patientInfo?: Record<string, any>;
  searchedItemListWithKey?: Record<string, any>;
  masterOptions?: Record<string, any[]>;
  // lab order
  labOrderQueue?: LabOrderQueueType;
  selectedLabOrderWorking?: CentralLabOrderSimplifiedSerializer;
  specimenCollectorMasterData?: SpecimenCollectorMasterDataType;
  selectedContainer?: SpecimenContainerSerializer | null;
  specimenLogs?: CentralLabOrderItemActionLogSerializer[];
  // lab report
  LabReportSequence?: LabReportSequence["LabReportSequence"];
  permissionLabResultConfidential?: boolean;
  languageUX?: string;
  // callback
  onClose?: (changed: boolean) => any;
  // config
  isMeBilling?: boolean;
};

const CardMeLabOrder = (props: CardMeLabOrderProps) => {
  const intl = useIntl();
  const [searchText, setSearchText] = useState<string>("");
  const [openModInfo, setOpenModInfo] = useState<boolean>(false);
  const [openModLabOrder, setOpenModLabOrder] = useState<boolean>(false);
  const [openModLabReport, setOpenModLabReport] = useState<boolean>(false);

  const cardSpecimenCollectorRef = useRef<any>();

  useEffect(() => {
    props.onEvent({
      message: "GetLabOrderQueue",
      params: {
        action: "search",
        hn: props.selectedPatient?.hn,
        restart: true,
      },
    });

    props.onEvent({ message: "SpecimenCollectorMasterData" });

    props.setProp("selectedLabOrderWorking", {});
  }, []);

  const lapOrderList = useMemo(() => {
    return (props?.labOrderQueue?.labOrderQueueList || [])
      .sort((a: any, b: any) => (a.highest_urgency <= b.highest_urgency ? 1 : -1))
      .map((item: any) => {
        const formatText = (text: string) => {
          return text
            .replace(/<[^>]*>/g, "") // ลบ tag html ก่อน
            .replace(/^\[\d+\]\s*/, ""); // ลบ [ตัวเลข]
        };

        return {
          ...item,
          orderDate: <div>{item.date}</div>,
          accessNumber: <div>{item.full_access_number}</div>,
          serviceType: (
            <div
              style={{
                color: "#FE9500",
                ...(item.package_type_name === "Package" && { color: "#21ba45" }),
              }}
            >
              {item.package_type_name}
            </div>
          ),
          orderName: <div>{formatText(item.package_name)}</div>,
          arriveDate: <div>{item.arrive_time}</div>,
          action: (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ display: "flex" }}>
                <div style={{ marginRight: "20px" }}>
                  <Button
                    size="small"
                    style={{ background: "#91C56142" }}
                    onClick={() => {
                      props.onEvent({
                        message: "LabOrderArrive",
                        params: {
                          action: "getDataLabOrderArrive",
                          labOrder: item,
                        },
                      });

                      setOpenModLabOrder(true);
                    }}
                  >
                    {"รายละเอียด"}
                  </Button>
                </div>
                <div>
                  <Button
                    size="small"
                    style={{ background: "#FFDEAF" }}
                    onClick={() => {
                      props.setProp("selectedLabOrderWorking", item);
                      setOpenModLabReport(true);
                    }}
                  >
                    {"บันทึกผล"}
                  </Button>
                </div>
              </div>
            </div>
          ),
        };
      });
  }, [props?.labOrderQueue?.labOrderQueueList, props.selectedLabOrderWorking?.id]);

  const labCenterOptions = useMemo(() => {
    return [{ key: "ALL", value: "ALL", text: "ทุก Lab Center" }];
  }, []);

  const handleSearchBarcode = (text: string) => {
    props.onEvent({
      message: "ScanBarcodeStickerSpecimen",
      params: {
        action: "scan",
        searchText: text,
        specimenList: cardSpecimenCollectorRef.current?.specimenList || [],
        callback: (status: "SUCCESS" | "ERROR") => {
          if (status === "SUCCESS") {
            setSearchText("");
          } else {
            setSearchText(searchText.trim());
            setOpenModInfo(true);
          }
        },
      },
    });
  };

  const handleCloseModInfo = () => {
    setOpenModInfo(false);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const value = (e.target as any).value || "";

      handleSearchBarcode(value);
      setSearchText(value);
    }
  };

  const handleGetRowProps = (state: any, rowInfo: any) => {
    return {
      style: {
        backgroundColor:
          rowInfo?.original?.id && rowInfo?.original?.id === props.selectedLabOrderWorking?.id
            ? "#cccccc"
            : "white",
        color: rowInfo?.original?.highest_urgency === "STAT" ? "red" : "",
      },
      onClick: () => {
        props.onEvent({
          message: "LabOrderArrive",
          params: {
            action: "getDataLabOrderArrive",
            labOrder: rowInfo?.original,
          },
        });
      },
    };
  };

  const handleChangeData = (e: any, data: any) => {
    props.setProp(`labOrderQueue.${data.name}`, data.value);
  };

  const handleChangeDate = (type: string) => (date: any) => {
    props.setProp(`labOrderQueue.${type}`, date);
  };

  const handleCloseModLabOrder = () => {
    setOpenModLabOrder(false);

    props.setProp("selectedLabOrderWorking", {});
  };

  const handleCloseModLabReport = () => {
    setOpenModLabReport(false);
  };

  return (
    <div id="CardMeLabOrder" style={{ height: "94vh" }}>
      <CardMeLabOrderListUX
        isMeBilling={true}
        lapOrderList={lapOrderList}
        rowProps={handleGetRowProps}
        languageUX={props.languageUX}
        labItemStatusOptions={STATUS_OPTIONS}
        labCenterOptions={labCenterOptions}
        labOrderQueue={props.labOrderQueue}
        onChangeFilter={handleChangeData}
        onChangeStartDate={handleChangeDate("selectedStartDate")}
        onChangeEndDate={handleChangeDate("selectedEndDate")}
        tubeStickerID={
          <div style={{ display: "flex" }}>
            <Input
              value={searchText}
              placeholder="Tube Sticker ID"
              size="mini"
              onKeyPress={handleKeyPress}
              onChange={(e: any, v: any) => {
                setSearchText(v.value);
              }}
            ></Input>
            <Button
              icon="search"
              onClick={() => handleSearchBarcode(searchText)}
              style={{ background: "var(--primary-theme-color)", color: "white" }}
            ></Button>
          </div>
        }
        buttonSearch={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={() => {
              props.onEvent({
                message: "GetLabOrderQueue",
                params: {
                  action: "search",
                  hn: props.selectedEncounter?.hn,
                  buttonLoadKey: `${CARD_KEY}_search`,
                },
              });
            }}
            // data
            paramKey={`${CARD_KEY}_search`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_KEY}_search`]}
            // config
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid={true}
            size="small"
            title={"ค้นหา"}
          />
        }
      />

      <Modal onClose={handleCloseModLabReport} open={openModLabReport} size="fullscreen">
        <div style={{ padding: "10px" }}>
          <CardMeLabReport
            setProp={props.setProp}
            onEvent={props.onEvent}
            // seq
            runSequence={props.runSequence}
            LabReportSequence={props.LabReportSequence}
            // data
            patientInfo={props.patientInfo}
            selectedPatient={props.selectedPatient}
            selectedLabOrderWorking={props.selectedLabOrderWorking}
            permissionLabResultConfidential={props.permissionLabResultConfidential}
            onClose={handleCloseModLabReport}
            // CommonInterface
            errorMessage={props.errorMessage}
            successMessage={props.successMessage}
            buttonLoadCheck={props.buttonLoadCheck}
            searchedItemListWithKey={props.searchedItemListWithKey}
            // options
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        </div>
      </Modal>

      <ModInfo
        open={openModInfo}
        titleColor={"red"}
        titleName={intl.formatMessage({ id: "ไม่พบรหัสสิ่งส่งตรวจ (Specimen ID)" })}
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
      >
        {/* <ErrorMessage error={openModError.error} /> */}
      </ModInfo>

      <Modal onClose={handleCloseModLabOrder} open={openModLabOrder} size={"fullscreen"}>
        <div style={{ padding: "10px 20px 10px 20px" }}>
          <CardSpecimenCollector
            ref={cardSpecimenCollectorRef}
            // function
            setProp={props.setProp}
            onEvent={props.onEvent}
            // common data
            errorMessage={props.errorMessage}
            successMessage={props.successMessage}
            buttonLoadCheck={props.buttonLoadCheck}
            django={props.django}
            // data
            selectedLabOrderWorking={props.selectedLabOrderWorking}
            selectedContainer={props.selectedContainer}
            specimenLogs={props.specimenLogs}
            specimenCollectorMasterData={props.specimenCollectorMasterData}
            selectedDivision={props.selectedDivision}
            selectedEncounter={props.selectedEncounter}
            labOrderQueue={props.labOrderQueue}
            onClose={handleCloseModLabOrder}
            // options
            masterOptions={props.masterOptions}
            // config
            isMeBilling={true}
            languageUX={props.languageUX}
          />
        </div>
      </Modal>
    </div>
  );
};

CardMeLabOrder.displayName = "CardMeLabOrder";

export default memo(CardMeLabOrder);
